/**
 * AI CONTEXT: business-logic - Credit distribution and tracking service
 * Journal: J-20250809-01
 * 
 * Purpose:
 * - Manage monthly issuance and credit history
 * - Integrate with GiveawayContext operations
 * - Provide simple in-memory tracking for MVP
 * 
 * Future considerations:
 * - Integrate with payment processing for purchases
 * - Add expiration and transfers
 * - Persist history server-side
 */

import { useGiveaway } from '../contexts/GiveawayContext';
import { authorizedFetch, isApiConfigured } from './apiClient';

/**
 * Credit Management Service
 * Provides functionality for managing user credits throughout the application
 */
class CreditService {
  constructor() {
    this.creditHistory = [];
    this.monthlyIssuanceLog = new Map();
  }

  /**
   * Admin: grant credits to a user by email and tier via API
   * @param {string} email
   * @param {number} amount
   * @param {('Bronze'|'Silver'|'Gold'|'Platinum')} tier
   */
  async grantCreditsByEmail(email, amount, tier = 'Bronze') {
    // AI CONTEXT: architecture - Admin credits grant via secured API
    // Reasoning: Persist credits server-side, independent of client state
    // Constraints: Requires JWT (admin user, name==='admin') and API base URL
    // Future: Add reasons/audit logging and rate limiting
    if (!isApiConfigured()) throw new Error('API not configured');
    return authorizedFetch('/credits/grant', {
      method: 'POST',
      body: JSON.stringify({ email, amount, tier })
    });
  }

  /**
   * Admin: adjust (add/subtract) credits for a user by email and tier
   * @param {string} email
   * @param {number} amount Positive integer
   * @param {('Bronze'|'Silver'|'Gold'|'Platinum')} tier
   * @param {('add'|'subtract')} op
   */
  async adjustCreditsByEmail(email, amount, tier = 'Bronze', op = 'add') {
    // AI CONTEXT: admin - Adjust credits via secure API
    if (!isApiConfigured()) throw new Error('API not configured');
    return authorizedFetch('/credits/adjust', {
      method: 'POST',
      body: JSON.stringify({ email, amount, tier, op })
    });
  }

  /**
   * Award purchase credits for the authenticated user
   * @param {Array<{tier: string, quantity: number}>} items
   */
  async awardPurchaseCredits(items) {
    // AI CONTEXT: business-logic - Persist earned credits on checkout
    // Reasoning: Ensure server-side truth for credits after purchases
    // Constraints: Requires user JWT; tolerate offline/mock during dev
    // Future: Validate items against product catalog server-side
    if (!isApiConfigured()) return { skipped: true };
    return authorizedFetch('/credits/award', {
      method: 'POST',
      body: JSON.stringify({ items })
    });
  }

  /**
   * Fetch credits for the authenticated user
   */
  async getMyCredits() {
    console.log('🔍 CreditService: getMyCredits called');
    if (!isApiConfigured()) {
      console.warn('🔍 CreditService: API not configured, returning null');
      return null;
    }
    console.log('🔍 CreditService: Calling authorizedFetch /credits');
    const result = await authorizedFetch('/credits', { method: 'GET' });
    console.log('🔍 CreditService: authorizedFetch result:', result);
    return result;
  }

  /**
   * Consume credits for the authenticated user
   * @param {('Bronze'|'Silver'|'Gold'|'Platinum')} tier
   * @param {number} amount
   */
  async consumeCredits(tier, amount = 1) {
    if (!isApiConfigured()) return { skipped: true };
    return authorizedFetch('/credits/consume', {
      method: 'POST',
      body: JSON.stringify({ tier, amount })
    });
  }

  /**
   * Issue monthly credits to subscribers
   * @param {string} userId - User ID
   * @param {Function} issueMonthlyCredit - Function from GiveawayContext
   * @param {Function} getUserCredits - Function from GiveawayContext
   * @returns {boolean} - Success status
   */
  issueMonthlyCredits(userId, issueMonthlyCredit, getUserCredits) {
    try {
      const now = new Date();
      const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      
      // Check if already issued this month
      const lastIssued = this.monthlyIssuanceLog.get(userId);
      if (lastIssued === currentMonth) {
        return false; // Already issued this month
      }

      // Issue the credit
      issueMonthlyCredit(userId);
      
      // Log the issuance
      this.monthlyIssuanceLog.set(userId, currentMonth);
      
      // Add to credit history
      this.creditHistory.push({
        userId,
        type: 'monthly_issuance',
        amount: 1,
        tier: 'Bronze',
        date: now.toISOString(),
        description: 'Monthly subscriber credit'
      });

      return true;
    } catch (error) {
      console.error('Error issuing monthly credits:', error);
      return false;
    }
  }

  /**
   * Get credit history for a user
   * @param {string} userId - User ID
   * @returns {Array} - Credit history
   */
  getCreditHistory(userId) {
    return this.creditHistory.filter(entry => entry.userId === userId);
  }

  /**
   * Check if user is eligible for monthly credits
   * @param {Object} user - User object
   * @param {Function} getUserCredits - Function from GiveawayContext
   * @returns {boolean} - Eligibility status
   */
  isEligibleForMonthlyCredits(user, getUserCredits) {
    if (!user?.isSubscriber) return false;
    
    const credits = getUserCredits(user.id);
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    
    if (credits.monthlyCreditIssued) {
      const lastMonth = credits.monthlyCreditIssued.substring(0, 7);
      return lastMonth !== currentMonth;
    }
    
    return true; // Never issued before
  }

  /**
   * Get credit statistics for a user
   * @param {string} userId - User ID
   * @param {Function} getUserCredits - Function from GiveawayContext
   * @returns {Object} - Credit statistics
   */
  getCreditStats(userId, getUserCredits) {
    const credits = getUserCredits(userId);
    const history = this.getCreditHistory(userId);
    
    const totalEarned = history.reduce((sum, entry) => sum + entry.amount, 0);
    const totalUsed = history.filter(entry => entry.type === 'used').reduce((sum, entry) => sum + entry.amount, 0);
    
    return {
      current: {
        bronze: credits.bronzeCredits || 0,
        silver: credits.silverCredits || 0,
        gold: credits.goldCredits || 0,
        platinum: credits.platinumCredits || 0
      },
      totalEarned,
      totalUsed,
      available: totalEarned - totalUsed,
      history: history.slice(-10) // Last 10 entries
    };
  }

  /**
   * Process automatic credit distribution
   * @param {Array} users - Array of user objects
   * @param {Function} issueMonthlyCredit - Function from GiveawayContext
   * @param {Function} getUserCredits - Function from GiveawayContext
   * @returns {Array} - Users who received credits
   */
  processAutomaticCredits(users, issueMonthlyCredit, getUserCredits) {
    const recipients = [];
    
    users.forEach(user => {
      if (this.isEligibleForMonthlyCredits(user, getUserCredits)) {
        const success = this.issueMonthlyCredits(user.id, issueMonthlyCredit, getUserCredits);
        if (success) {
          recipients.push(user);
        }
      }
    });
    
    return recipients;
  }

  /**
   * Get next credit issuance date
   * @param {Object} user - User object
   * @param {Function} getUserCredits - Function from GiveawayContext
   * @returns {Date} - Next issuance date
   */
  getNextCreditIssuanceDate(user, getUserCredits) {
    if (!user?.isSubscriber) return null;
    
    const credits = getUserCredits(user.id);
    if (!credits.monthlyCreditIssued) return new Date();
    
    const lastIssued = new Date(credits.monthlyCreditIssued);
    const nextIssuance = new Date(lastIssued);
    nextIssuance.setMonth(nextIssuance.getMonth() + 1);
    
    return nextIssuance;
  }
}

// Export singleton instance
export const creditService = new CreditService();

// Hook for using credit service
export const useCreditService = () => {
  const { issueMonthlyCredit, getUserCredits } = useGiveaway();
  
  return {
    issueMonthlyCredits: (userId) => creditService.issueMonthlyCredits(userId, issueMonthlyCredit, getUserCredits),
    getCreditHistory: (userId) => creditService.getCreditHistory(userId),
    isEligibleForMonthlyCredits: (user) => creditService.isEligibleForMonthlyCredits(user, getUserCredits),
    getCreditStats: (userId) => creditService.getCreditStats(userId, getUserCredits),
    getNextCreditIssuanceDate: (user) => creditService.getNextCreditIssuanceDate(user, getUserCredits)
  };
};
